import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, FileUp } from "lucide-react";
import { Shimmer } from "../components/Shimmer/Shimmer";
import { UploadPolicyVersionModal } from "../components/UploadPolicyVersionModal/UploadPolicyVersionModal";
import {
  fetchPolicyCheckCompanyById,
  fetchPolicyCheckResult,
  isPolicyCheckPendingStatus,
  mapPolicyReportToTaskItems,
  type PolicyCheckCompany,
  type PolicyCheckReport,
  type PolicyCheckTaskListItem,
} from "../api/policyCheck";
import { usePolicyCheckTasks } from "../hooks/usePolicyCheckTasks";
import { useTariffAccess } from "../hooks/useTariffAccess";
import { isAdminTabId } from "../components/AdminWorkspace/adminTabs";
import { formatDate, getStatusLabel } from "./RequestDetail/requestDetailUtils";
import type { PolicyCheckLocationState } from "./policyCheckLocationState";
import "./RequestDetail.scss";

const POLL_INTERVAL_MS = 5000;

export const PolicyCheckDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as PolicyCheckLocationState | null) ?? null;
  const tariffAccess = useTariffAccess();

  const [company, setCompany] = useState<PolicyCheckCompany | null>(() =>
    locationState?.companyName || locationState?.inn
      ? {
          id,
          companyName: locationState.companyName,
          inn: locationState.inn,
        }
      : null
  );
  const [companyLoading, setCompanyLoading] = useState(!company);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [report, setReport] = useState<PolicyCheckReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFileId, setPendingFileId] = useState<string | null>(null);

  const { data, totalPages, loading, error, refetch } = usePolicyCheckTasks({
    id,
    page: 0,
    pageSize: 50,
  });

  const selectedTask: PolicyCheckTaskListItem | null = data[selectedIndex] ?? null;
  const selectedStatus = report?.status ?? selectedTask?.status;
  const isPending = isPolicyCheckPendingStatus(selectedStatus);
  const uploadDisabled = !tariffAccess.canPolicyCheck;

  const handleBack = useCallback(() => {
    const adminTab = locationState?.adminTab;
    if (isAdminTabId(adminTab)) {
      navigate({ pathname: "/", search: `?tab=${adminTab}` });
      return;
    }
    navigate({ pathname: "/", search: "?tab=policy-checks" });
  }, [locationState?.adminTab, navigate]);

  useEffect(() => {
    if (!id) return;
    if (company?.companyName && company?.inn) {
      setCompanyLoading(false);
      return;
    }

    let cancelled = false;
    setCompanyLoading(true);
    fetchPolicyCheckCompanyById(id)
      .then((item) => {
        if (!cancelled) setCompany(item);
      })
      .catch(() => {
        if (!cancelled) setCompany(null);
      })
      .finally(() => {
        if (!cancelled) setCompanyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [company?.companyName, company?.inn, id]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [id]);

  useEffect(() => {
    if (selectedIndex >= data.length && data.length > 0) {
      setSelectedIndex(0);
    }
  }, [data.length, selectedIndex]);

  useEffect(() => {
    if (!pendingFileId || loading) return;
    const index = data.findIndex((item) => item.id === pendingFileId);
    if (index >= 0) {
      setSelectedIndex(index);
      setPendingFileId(null);
      return;
    }
    setSelectedIndex(0);
    setPendingFileId(null);
  }, [data, loading, pendingFileId]);

  useEffect(() => {
    const fileId = selectedTask?.id?.trim();
    if (!fileId) {
      setReport(null);
      setReportLoading(false);
      return;
    }

    if (isPolicyCheckPendingStatus(selectedTask?.status)) {
      setReport(null);
      setReportLoading(false);
      return;
    }

    let cancelled = false;
    setReportLoading(true);

    fetchPolicyCheckResult(fileId)
      .then((item) => {
        if (!cancelled) setReport(item);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTask?.id, selectedTask?.status]);

  useEffect(() => {
    if (!selectedTask?.id || !isPending) return;

    const timer = window.setInterval(() => {
      refetch({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isPending, refetch, selectedTask?.id]);

  if (!id) return null;

  const tasks = report ? mapPolicyReportToTaskItems(report) : [];
  const pageLoading = loading || companyLoading;
  const startDateTime = report?.startDateTime || selectedTask?.startDateTime;
  const endDateTime = report?.endDateTime || selectedTask?.endDateTime;

  return (
    <div className="detail-page">
      <div className="detail-container">
        <div className="detail-top-bar">
          <button type="button" className="detail-back" onClick={handleBack}>
            <ArrowLeft size={20} />
            <span>Назад</span>
          </button>
          {!pageLoading && !error ? (
            <div className="detail-top-actions">
              <button
                type="button"
                className="detail-schedule-btn"
                disabled={uploadDisabled}
                onClick={() => setShowUploadModal(true)}
              >
                <FileUp size={18} />
                <span>Загрузить новую версию</span>
              </button>
            </div>
          ) : null}
        </div>

        {pageLoading ? (
          <Shimmer rows={3} />
        ) : error ? (
          <div className="detail-card">
            <span className="detail-value">{error}</span>
            <button type="button" className="detail-schedule-btn" onClick={() => refetch()}>
              Повторить
            </button>
          </div>
        ) : (
          <>
            <div className="detail-card">
              <div className="detail-row">
                <span className="detail-label">Компания</span>
                <span className="detail-value">{company?.companyName?.trim() || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">ИНН</span>
                <span className="detail-value">{company?.inn?.trim() || "—"}</span>
              </div>
            </div>

            {data.length === 0 ? (
              <div className="detail-card">
                <span className="detail-value">Нет проверок</span>
              </div>
            ) : selectedTask ? (
              <div className="detail-deck">
                <div className="detail-deck-content">
                  {selectedIndex > 0 ? (
                    <button
                      type="button"
                      className="detail-deck-arrow detail-deck-arrow-hide-mobile"
                      onClick={() => setSelectedIndex((index) => Math.max(0, index - 1))}
                      aria-label="Предыдущая"
                    >
                      <ChevronLeft size={28} />
                    </button>
                  ) : null}

                  <div className="detail-card" style={{ flex: 1, minWidth: 0 }}>
                    <div className="detail-row">
                      <span className="detail-label">Файл</span>
                      <span className="detail-value">
                        {selectedTask.policyFileName?.trim() || "—"}
                      </span>
                    </div>
                    <div className="detail-dates-row">
                      <div className="detail-row">
                        <span className="detail-label">Начало</span>
                        <span className="detail-value">
                          {startDateTime ? formatDate(startDateTime) : "—"}
                        </span>
                      </div>
                      {endDateTime ? (
                        <div className="detail-row">
                          <span className="detail-label">Окончание</span>
                          <span className="detail-value">{formatDate(endDateTime)}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Статус</span>
                      <span className="detail-value">
                        {selectedStatus ? getStatusLabel(selectedStatus) : "—"}
                      </span>
                    </div>

                    {isPending ? (
                      <div className="detail-report-placeholder">
                        <div className="detail-report-progress">
                          <div className="detail-report-progress-bar" />
                        </div>
                        После выполнения проверки, здесь будут отображены результаты
                      </div>
                    ) : reportLoading ? (
                      <Shimmer rows={4} />
                    ) : selectedStatus === "error" ? (
                      <div className="detail-report-placeholder detail-report-error">
                        При проверке произошла ошибка, повторите позднее
                      </div>
                    ) : tasks.length > 0 ? (
                      <div className="detail-report-tasks detail-report-tasks--tall">
                        <table className="detail-tasks-table">
                          <thead>
                            <tr>
                              <th>Проверка</th>
                              <th>Результат</th>
                              <th>Детали</th>
                              <th>Рекомендация</th>
                              <th>Ответственность</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tasks.map((task, index) => (
                              <tr
                                key={task.taskId || `${task.checkName}-${task.result}-${index}`}
                                className={
                                  task.color ? `detail-task-row--${task.color}` : undefined
                                }
                              >
                                <td>{task.checkNameDescription || task.checkName || "—"}</td>
                                <td>{task.resultDescription || task.result || "—"}</td>
                                <td>{task.details?.trim() || "—"}</td>
                                <td>{task.suggestion || "—"}</td>
                                <td>{task.responsibility || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="detail-report-placeholder">Нет результатов</div>
                    )}
                  </div>

                  {selectedIndex < data.length - 1 ? (
                    <button
                      type="button"
                      className="detail-deck-arrow detail-deck-arrow-hide-mobile"
                      onClick={() =>
                        setSelectedIndex((index) => Math.min(data.length - 1, index + 1))
                      }
                      aria-label="Следующая"
                    >
                      <ChevronRight size={28} />
                    </button>
                  ) : null}
                </div>

                {(data.length > 1 || totalPages > 1) && (
                  <div className="detail-deck-nav">
                    {selectedIndex > 0 ? (
                      <button
                        type="button"
                        className="detail-deck-nav-arrow"
                        onClick={() => setSelectedIndex((index) => Math.max(0, index - 1))}
                        aria-label="Предыдущая"
                      >
                        <ChevronLeft size={24} />
                      </button>
                    ) : (
                      <span style={{ width: 48, minWidth: 48 }} />
                    )}
                    <span className="detail-deck-counter">{selectedIndex + 1}</span>
                    {selectedIndex < data.length - 1 ? (
                      <button
                        type="button"
                        className="detail-deck-nav-arrow"
                        onClick={() =>
                          setSelectedIndex((index) => Math.min(data.length - 1, index + 1))
                        }
                        aria-label="Следующая"
                      >
                        <ChevronRight size={24} />
                      </button>
                    ) : (
                      <span style={{ width: 48, minWidth: 48 }} />
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      {showUploadModal ? (
        <UploadPolicyVersionModal
          policyCheckRequestId={id}
          onClose={() => setShowUploadModal(false)}
          onCreated={(fileId) => {
            setShowUploadModal(false);
            setPendingFileId(fileId);
            setSelectedIndex(0);
            setReport(null);
            refetch({ silent: true });
          }}
        />
      ) : null}
    </div>
  );
};
